import { useMemo } from "react";

import { useLocation } from "react-router-dom";

import { ROUTE_CONFIG } from "@/routes/config";

interface UrlComposerItem {
	id: number | string;
	title: string;
}

interface ResolveComposeBadgeComposersParams {
	urlComposersParam: string | null;
	userId?: number;
}

/**
 * Resolves which composer ids the sidebar compose badge count should be
 * filtered by, mirroring the composer selection of the Compose page:
 * - an explicit selection (taken from the compose `composers` URL param) wins
 * - an "All" selection or a missing user id means no filter (`undefined`),
 *   which also includes records without a composer (pre-feature data)
 * - otherwise defaults to the logged-in user ("Me")
 */
export const resolveComposeBadgeComposers = ({
	urlComposersParam,
	userId,
}: ResolveComposeBadgeComposersParams): number[] | undefined => {
	if (urlComposersParam) {
		try {
			const parsed = JSON.parse(urlComposersParam) as UrlComposerItem[];

			if (parsed.some((composer) => composer.id === "all")) {
				return undefined;
			}

			const composerIds = parsed
				.map((composer) => composer.id)
				.filter((id): id is number => typeof id === "number");

			return composerIds.length > 0 ? composerIds : undefined;
		} catch {
			// Malformed URL state — fall back to the default selection below
		}
	}

	return userId ? [userId] : undefined;
};

/**
 * Composer filter for the sidebar compose badge. While the user is on the
 * Compose page the badge follows the composer selection stored in the URL;
 * everywhere else it falls back to the Compose page's default ("Me").
 * Unlike the inbox badge there is no async dependency — every Signalist user
 * qualifies as a composer.
 */
export const useComposeBadgeComposers = (userId?: number): number[] | undefined => {
	const location = useLocation();

	const urlComposersParam =
		location.pathname === ROUTE_CONFIG.MESSAGE_CENTER_COMPOSE
			? new URLSearchParams(location.search).get("composers")
			: null;

	return useMemo(
		() => resolveComposeBadgeComposers({ urlComposersParam, userId }),
		[urlComposersParam, userId]
	);
};
